// SPDX-License-Identifier: AGPL-3.0-or-later

import type {MarkerState} from '@app/features/ui/components/slider/SliderTypes';

export const SLIDER_TRACK_PADDING = 5;

interface BuildMarkerStateOptions {
	markers?: Array<number>;
	minValue: number;
	maxValue: number;
	value: number;
	equidistant: boolean;
}

interface PointerValueOptions {
	clientX: number;
	rect: DOMRect;
	markerState: MarkerState;
	trackPadding?: number;
	step?: number;
	dragGrabOffset?: number;
}

interface TrackClickValueOptions extends PointerValueOptions {
	stickToMarkers: boolean;
}

interface MarkerDragOptions {
	clientX: number;
	startClientX: number;
	rect: DOMRect;
	markerState: MarkerState;
	startMarkerIndex: number;
	equidistant: boolean;
}

interface KeyboardValueOptions {
	key: string;
	value: number;
	stickToMarkers: boolean;
	markerState: MarkerState;
	minValue: number;
	maxValue: number;
	step?: number;
}

export function applyStep(value: number, step?: number): number {
	if (step === undefined || step === 0) return value;
	return Math.round(value / step) * step;
}

export function clamp(value: number, min: number, max: number): number {
	return Math.max(min, Math.min(max, value));
}

export function findClosestMarkerIndex(value: number, markerArray: Array<number>): number {
	if (markerArray.length === 0) return 0;
	let previousDiff = 0;
	for (let i = 0; i < markerArray.length; i++) {
		const currentMarker = markerArray[i];
		if (value === currentMarker) {
			return i;
		}
		if (value < currentMarker) {
			if (previousDiff === 0) {
				return i;
			}
			if (currentMarker - value < previousDiff) {
				return i;
			}
			return i - 1;
		}
		previousDiff = value - currentMarker;
	}
	return markerArray.length - 1;
}

export function buildMarkerState({
	markers,
	minValue,
	maxValue,
	value,
	equidistant,
}: BuildMarkerStateOptions): MarkerState {
	if (!markers || markers.length === 0) {
		return {
			min: minValue,
			max: maxValue,
			range: maxValue - minValue,
			sortedMarkers: [],
			markerPositions: [],
		};
	}
	const sortedMarkers = [...markers].sort((a, b) => a - b);
	const closestMarkerIndex = findClosestMarkerIndex(value, sortedMarkers);
	const min = sortedMarkers[0];
	const max = sortedMarkers[sortedMarkers.length - 1];
	const range = max - min;
	const markerPositions =
		sortedMarkers.length === 1
			? [0]
			: equidistant
				? sortedMarkers.map((_, i) => i * (100 / (sortedMarkers.length - 1)))
				: sortedMarkers.map((marker) => scaleValue({min, range}, marker));
	return {
		min,
		max,
		range,
		sortedMarkers,
		markerPositions,
		closestMarkerIndex,
	};
}

export function scaleValue(markerState: Pick<MarkerState, 'min' | 'range'>, value: number): number {
	if (markerState.range === 0) return 0;
	return (100 * (value - markerState.min)) / markerState.range;
}

export function unscaleValue(markerState: Pick<MarkerState, 'min' | 'range'>, percent: number): number {
	if (markerState.range === 0) return markerState.min;
	return (percent * markerState.range) / 100 + markerState.min;
}

export function getScaledSliderValue(
	value: number,
	markerState: MarkerState,
	stickToMarkers: boolean,
	newClosestIndex: number | null,
): number {
	if (!stickToMarkers) {
		return scaleValue(markerState, value);
	}
	if (markerState.markerPositions.length === 0) {
		return 0;
	}
	if (newClosestIndex != null) {
		return markerState.markerPositions[newClosestIndex];
	}
	if (markerState.closestMarkerIndex !== undefined) {
		return markerState.markerPositions[markerState.closestMarkerIndex];
	}
	return 0;
}

export function getTrackClickValue({
	clientX,
	rect,
	markerState,
	stickToMarkers,
	trackPadding = SLIDER_TRACK_PADDING,
	step,
}: TrackClickValueOptions): {value: number; markerIndex: number | null} {
	const clickX = clientX - rect.left - trackPadding;
	const trackWidth = rect.width - trackPadding * 2;
	const percentage = trackWidth <= 0 ? 0 : clamp((clickX / trackWidth) * 100, 0, 100);
	if (stickToMarkers && markerState.markerPositions.length > 0) {
		const markerIndex = findClosestMarkerIndex(percentage, markerState.markerPositions);
		return {
			value: markerState.sortedMarkers[markerIndex],
			markerIndex,
		};
	}
	return {
		value: applyStep(unscaleValue(markerState, percentage), step),
		markerIndex: null,
	};
}

export function getSmoothDragValue({
	clientX,
	rect,
	markerState,
	trackPadding = SLIDER_TRACK_PADDING,
	step,
	dragGrabOffset = 0,
}: PointerValueOptions): number | null {
	const trackWidth = rect.right - rect.left - trackPadding * 2;
	if (trackWidth <= 0) return null;
	const targetThumbX = clientX - dragGrabOffset - rect.left - trackPadding;
	const ratio = clamp(targetThumbX / trackWidth, 0, 1);
	const rawValue = markerState.range === 0 ? markerState.min : markerState.min + ratio * markerState.range;
	return applyStep(clamp(rawValue, markerState.min, markerState.max), step);
}

export function getSmoothDragGrabOffset(
	clientX: number,
	rect: DOMRect,
	value: number,
	markerState: MarkerState,
	trackPadding: number = SLIDER_TRACK_PADDING,
): number {
	const trackWidth = rect.width - trackPadding * 2;
	const valueRatio = markerState.range === 0 ? 0 : (value - markerState.min) / markerState.range;
	const thumbScreenX = rect.left + trackPadding + clamp(valueRatio, 0, 1) * trackWidth;
	return clientX - thumbScreenX;
}

export function getDraggedMarkerIndex({
	clientX,
	startClientX,
	rect,
	markerState,
	startMarkerIndex,
	equidistant,
}: MarkerDragOptions): number | null {
	if (clientX <= rect.left || clientX >= rect.right) {
		return null;
	}
	const sliderWidth = rect.right - rect.left;
	if (sliderWidth <= 0) {
		return null;
	}
	const deltaPixels = clientX - startClientX;
	const percentMoved = deltaPixels / sliderWidth;
	const destinationPosition = markerState.markerPositions[startMarkerIndex] + percentMoved * 100;
	if (equidistant) {
		return findClosestMarkerIndex(destinationPosition, markerState.markerPositions);
	}
	return findClosestMarkerIndex(unscaleValue(markerState, destinationPosition), markerState.sortedMarkers);
}

export function getKeyboardValue({
	key,
	value,
	stickToMarkers,
	markerState,
	minValue,
	maxValue,
	step,
}: KeyboardValueOptions): number | null {
	const keyStep =
		stickToMarkers && markerState.sortedMarkers.length > 0
			? (markerState.max - markerState.min) / Math.max(1, markerState.sortedMarkers.length - 1)
			: (step ?? (maxValue - minValue) / 100);
	switch (key) {
		case 'ArrowLeft':
		case 'ArrowDown':
			if (stickToMarkers && markerState.sortedMarkers.length > 0) {
				const currentIndex = findClosestMarkerIndex(value, markerState.sortedMarkers);
				return markerState.sortedMarkers[Math.max(0, currentIndex - 1)];
			}
			return applyStep(clamp(value - keyStep, minValue, maxValue), step);
		case 'ArrowRight':
		case 'ArrowUp':
			if (stickToMarkers && markerState.sortedMarkers.length > 0) {
				const currentIndex = findClosestMarkerIndex(value, markerState.sortedMarkers);
				return markerState.sortedMarkers[Math.min(markerState.sortedMarkers.length - 1, currentIndex + 1)];
			}
			return applyStep(clamp(value + keyStep, minValue, maxValue), step);
		case 'Home':
			return stickToMarkers && markerState.sortedMarkers.length > 0 ? markerState.sortedMarkers[0] : minValue;
		case 'End':
			return stickToMarkers && markerState.sortedMarkers.length > 0
				? markerState.sortedMarkers[markerState.sortedMarkers.length - 1]
				: maxValue;
		default:
			return null;
	}
}
